import { skyCinematicFrame } from './117-sky-cinematics.mjs';
import { SKY_STAGES, SKY_FINAL_GUARDIANS } from '../../../packages/domain/src/76-sky-campaign.mjs';
import { skyBossDesign } from '../../../packages/domain/src/85-sky-boss-designs.mjs';
import { skyBossAssetKey, skyStageArtVariant } from './134-sky-asset-manifest.mjs';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';

const clamp = (x) => Math.max(0, Math.min(1, x));

/** Extend each stage arrival. A device study of its actual encounter
 * fades to black before the existing flight intro; visible time still belongs
 * to the controller, so pause, skip and reduced motion share its lifecycle. */
export function skyPrototypeCinematicFrame(kind, age, reduced = false, sector = 0, count = 1) {
  const lead = reduced ? 0.8 * count : 4.5 + Math.max(0, count - 1) * 1.5;
  if (kind !== 'arrival') return skyCinematicFrame(kind, age, reduced);
  // The controller uses duration to skip or hold the completed presentation.
  // Report the complete wrapper timeline in both branches, not the flight tail.
  const duration = lead + skyCinematicFrame(kind, 0, reduced).duration;
  if (age >= lead)
    return {
      ...skyCinematicFrame(kind, age - lead, reduced),
      duration,
      done: age >= duration,
      flightAge: age - lead,
    };
  return {
    ...skyCinematicFrame(kind, 0, reduced),
    duration,
    done: false,
    briefing: true,
    briefingTime: reduced ? 0 : Math.max(0, age),
    briefingReduced: reduced,
    briefingOpacity: reduced ? 1 : 1 - clamp((age - (lead - 0.7)) / 0.45),
    briefingIndex: Math.min(count - 1, Math.floor((age / lead) * count)),
    shot: 'briefing',
    settle: 0,
  };
}

/** Whole-image opacity over 1.25 visible seconds. Reduced motion presents the
 * complete projection immediately; repeat/rewind never accumulates fade state. */
export function skyHologramOpacity(time, reduced = false) {
  if (reduced) return 1;
  const progress = clamp((Number.isFinite(time) ? time : 0) / 1.25);
  return progress * progress * (3 - 2 * progress);
}

/** Isolated device display clones the actual injected dragon. Shared source
 * geometry/textures remain loader-owned; private materials, skeletons, mixer,
 * generated label textures and device geometry release with this presentation. */
export function createSkyHologram(T, source, briefing = {}) {
  if (!source?.scene) return null;
  const scene = new T.Scene();
  scene.background = new T.Color('#000000');
  const camera = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 20);
  camera.position.z = 5;
  const group = new T.Group(),
    model = cloneSkeleton(source.scene);
  const skeletons = new Set(),
    owned = new Set();
  const own = (resource) => {
    owned.add(resource);
    return resource;
  };
  const uniforms = { fade: { value: 1 }, projectionExtent: { value: 1 } };
  const material = own(
    new T.MeshStandardMaterial({
      color: '#8ce8d9',
      emissive: '#399f8d',
      emissiveIntensity: 0.45,
      roughness: 0.62,
      metalness: 0.18,
      transparent: true,
      opacity: 1,
      depthWrite: true,
      side: T.FrontSide,
    }),
  );
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = 'varying vec3 hologramPosition;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <project_vertex>',
      'hologramPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;\n#include <project_vertex>',
    );
    shader.fragmentShader =
      'varying vec3 hologramPosition;\nuniform float fade;\nuniform float projectionExtent;\n' +
      shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      `float rimGlow = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 2.5);
      vec2 lattice = fract(hologramPosition.xy / projectionExtent * 52.0) - 0.5;
      float dots = 1.0 - smoothstep(0.06, 0.19, length(lattice));
      outgoingLight = outgoingLight * 0.42 + vec3(0.25, 1.0, 0.82) *
        (rimGlow * 1.1 + dots * 0.3);
      // Retain faint faces and bright contours without a construction mask.
      diffuseColor.a *= fade * min(0.9, 0.22 + dots * 0.05 + rimGlow * 0.58);
      #include <opaque_fragment>`,
    );
  };
  material.customProgramCacheKey = () => 'sky-local-hologram-5';
  model.traverse((node) => {
    if (!node.isMesh) return;
    const original = Array.isArray(node.material) ? node.material[0] : node.material;
    const surface = own(material.clone());
    surface.side = original?.side ?? T.FrontSide;
    surface.normalMap = original?.normalMap ?? null;
    if (original?.normalScale) surface.normalScale.copy(original.normalScale);
    surface.onBeforeCompile = material.onBeforeCompile;
    surface.customProgramCacheKey = material.customProgramCacheKey;
    node.material = surface;
    node.castShadow = node.receiveShadow = false;
    if (node.isSkinnedMesh) skeletons.add(node.skeleton);
  });
  const key = new T.DirectionalLight('#b8f2ff', 2.2);
  key.position.set(-3, 4, 5);
  const edge = new T.DirectionalLight('#2b9bbc', 1);
  edge.position.set(3, 1, -2);
  scene.add(new T.HemisphereLight('#b4edff', '#0b182b', 0.65), key, edge);
  const mixer = new T.AnimationMixer(model);
  const clip =
    source.animations?.find((entry) => /fly|flight|idle/i.test(entry.name)) ??
    source.animations?.[0];
  if (clip) mixer.clipAction(clip).play();
  mixer.setTime(0);
  model.updateMatrixWorld(true);
  model.traverse((node) => {
    if (!node.isSkinnedMesh) return;
    node.skeleton.update();
    node.computeBoundingBox();
  });
  const bounds = new T.Box3().setFromObject(model),
    size = bounds.getSize(new T.Vector3());
  const scale = 1.2 / Math.max(size.x, size.y, size.z, 0.001);
  const center = bounds.getCenter(new T.Vector3());
  const normalization = new T.Group();
  normalization.scale.setScalar(scale);
  normalization.position.copy(center).multiplyScalar(-scale);
  normalization.add(model);
  const projection = new T.Group();
  projection.add(normalization);
  group.add(projection);
  const faceMaterial = own(new T.MeshBasicMaterial({ color: '#071720', transparent: true }));
  const rimMaterial = own(new T.MeshBasicMaterial({ color: '#253c47', transparent: true }));
  const panelGeometry = own(new T.PlaneGeometry(1, 1));
  const rim = new T.Mesh(panelGeometry, rimMaterial),
    face = new T.Mesh(panelGeometry, faceMaterial);
  rim.position.z = -1.2;
  face.position.z = -1.1;
  group.add(rim, face);
  const leaderMaterial = own(
    new T.LineBasicMaterial({
      color: '#5eacbd',
      transparent: true,
      opacity: 0.6,
      depthTest: false,
    }),
  );
  const crest = model.getObjectByName('weakpoint_core'),
    crestPosition = new T.Vector3();
  const leaders = [-1, 1].map((_, index) => {
    const geometry = own(
      new T.BufferGeometry().setAttribute(
        'position',
        new T.Float32BufferAttribute(new Float32Array(9), 3),
      ),
    );
    const line = new T.Line(geometry, leaderMaterial);
    line.name = index === 0 ? 'crest-callout' : 'wing-callout';
    line.renderOrder = 3;
    group.add(line);
    return line;
  });
  const labels = [];
  function label(text, y, color, width = 1.35) {
    if (typeof document === 'undefined') return;
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    const lines = [];
    for (const word of text.split(' ')) {
      if (!lines.length || lines.at(-1).length + word.length > 32) lines.push(word);
      else lines[lines.length - 1] += ` ${word}`;
    }
    canvas.height = 80 * lines.length + 16;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.fillStyle = color;
    context.font = '500 48px monospace';
    const longest = Math.max(...lines.map((line) => context.measureText(line).width), 1);
    const fontSize = Math.min(48, (48 * 980) / longest);
    context.font = `500 ${fontSize}px monospace`;
    context.textAlign = 'center';
    lines.forEach((line, index) => context.fillText(line, 512, 64 + index * 80));
    const map = own(new T.CanvasTexture(canvas));
    map.colorSpace = T.SRGBColorSpace;
    const mat = own(new T.SpriteMaterial({ map, transparent: true, depthTest: false }));
    const sprite = new T.Sprite(mat);
    sprite.position.set(0, y, 1);
    group.add(sprite);
    labels.push({ sprite, width, y, height: canvas.height });
  }
  label(briefing.name ?? 'AZURE CROWN', 0.77, '#b6f4ff');
  label(briefing.label ?? '01 / GUARDIAN INTELLIGENCE', 0.64, '#5ca3b4', 1.5);
  label(briefing.weakness ?? 'CRANIAL CREST', 0.43, '#f0d49a', 1.6);
  label(briefing.strength ?? 'ARMOURED WINGS', -0.43, '#79cddd', 1.6);
  label('TRACK SIGNAL // ACQUIRED', -0.83, '#62aebd', 1.5);
  group.visible = false;
  scene.add(group);
  let disposed = false;
  function draw(frame, aspect = 1) {
    group.visible = Boolean(frame?.briefing) && !disposed;
    if (!group.visible) return;
    const time = frame.briefingTime ?? 0,
      fade = frame.briefingOpacity ?? 1;
    camera.left = -aspect;
    camera.right = aspect;
    camera.updateProjectionMatrix();
    rim.scale.set(aspect * 1.94, 1.95, 1);
    face.scale.set(aspect * 1.88, 1.89, 1);
    const fit = Math.min(1.15, aspect * 1.35);
    projection.scale.setScalar(fit);
    projection.position.y = 0.04;
    projection.rotation.x = 0.22;
    projection.rotation.y = -0.3 + time * 0.35;
    uniforms.projectionExtent.value = fit;
    uniforms.fade.value = fade * skyHologramOpacity(time, frame.briefingReduced);
    faceMaterial.opacity = rimMaterial.opacity = fade;
    mixer.setTime(time);
    leaderMaterial.opacity = 0.6 * fade;
    const calloutX = Math.min(0.8, aspect * 0.38);
    group.updateMatrixWorld(true);
    if (crest) crest.getWorldPosition(crestPosition);
    leaders.forEach((line, index) => {
      const sign = index ? -1 : 1,
        y = index ? -0.37 : 0.37;
      const points = line.geometry.attributes.position;
      points.setXYZ(0, sign * calloutX, y, 1);
      points.setXYZ(1, sign * calloutX, y - sign * 0.06, 1);
      // The orthographic camera shares world X/Y with the label plane. Preserve
      // the animated socket projection while drawing its leader in front.
      line.visible = briefing.callouts !== false && (index !== 0 || Boolean(crest));
      if (index === 0 && crest) points.setXYZ(2, crestPosition.x, crestPosition.y, 1);
      else points.setXYZ(2, sign * fit * 0.22, y - sign * 0.15, 1);
      points.needsUpdate = true;
    });
    labels.forEach(({ sprite, width, y, height }, index) => {
      const callout = briefing.callouts !== false && (index === 2 || index === 3);
      const w = Math.min(width, aspect * (callout ? 1.5 : 1.75));
      sprite.scale.set(w, (w * height) / 1024, 1);
      sprite.position.x = callout ? (index === 2 ? calloutX : -calloutX) : 0;
      sprite.position.y = y;
      sprite.material.opacity = fade;
    });
  }
  return {
    group,
    scene,
    camera,
    draw,
    dispose() {
      if (disposed) return;
      disposed = true;
      scene.remove(group);
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
      for (const skeleton of skeletons) skeleton.dispose();
      for (const resource of owned) resource.dispose();
    },
  };
}

/** Stage preparation owns projection readiness. Cached projections clone only
 * actual loaded art; every stage timeline is stable before that art resolves.
 * Bonus routes truthfully brief their megafauna instead of inventing a boss. */
export function createSkyBriefings(T, art) {
  const prepared = new Map(),
    warmed = new Set(),
    warming = new Map();
  let disposed = false;
  const key = (state) => `${state.sector}:${skyStageArtVariant(state) ?? ''}`;
  function descriptors(state) {
    const stage = SKY_STAGES[state.sector];
    if (!stage) return [];
    const bosses = art.stageAssets(state).bosses;
    const encounters = stage.kind === 'rush' ? SKY_FINAL_GUARDIANS : [stage];
    return (bosses.length ? bosses : [{ bonus: true }]).map((boss, index) => {
      const design = skyBossDesign(boss.design),
        encounter =
          boss.artVariant === 'gilded-leviathan'
            ? SKY_STAGES.find((entry) => entry.encounterId === 'gilded-leviathan')
            : (encounters[index] ?? stage);
      return {
        key: boss.bonus ? 'mammoth' : skyBossAssetKey(boss),
        name: boss.bonus ? stage.name : encounter.bossName,
        label: `${String(state.sector + 1).padStart(2, '0')} / ${boss.bonus ? 'BONUS ROUTE' : 'GUARDIAN INTELLIGENCE'}`,
        weakness: boss.bonus
          ? 'COLLECT SUPPLIES / CLEAR THE ROUTE'
          : (encounter.briefing?.weakness ??
            design?.weakness ??
            'CONTROL LENSES → PRESSURE CORE → WOUND'),
        strength: boss.bonus
          ? 'MEGAFAUNA SUPPLY CARRIERS'
          : (encounter.briefing?.strength ?? design?.strength ?? 'ARMOURED HULL / ESCORT DOLPHINS'),
        callouts: false,
      };
    });
  }
  return {
    prepare(state) {
      if (disposed || prepared.has(key(state))) return;
      const entries = descriptors(state);
      if (!entries.length || entries.some((entry) => !art.models[entry.key])) return;
      prepared.set(
        key(state),
        entries.map((entry) => createSkyHologram(T, art.models[entry.key], entry)),
      );
    },
    async warm(state, renderer, compile) {
      const id = key(state),
        entries = prepared.get(id);
      if (!entries || warmed.has(id)) return;
      if (warming.has(id)) return warming.get(id);
      const promise = (async () => {
        for (const projection of entries) {
          projection.draw({ briefing: true, briefingTime: 0 }, 1);
          projection.scene.traverse((node) => {
            for (const value of Object.values(node.material ?? {}))
              if (value?.isTexture) renderer.initTexture(value);
          });
          await compile(projection.scene, undefined, projection.camera);
          if (disposed || prepared.get(id) !== entries)
            throw new DOMException('Briefing closed', 'AbortError');
        }
        warmed.add(id);
      })().finally(() => warming.delete(id));
      warming.set(id, promise);
      return promise;
    },
    frame(kind, age, reduced, sector, durations, variant = null) {
      if (kind !== 'arrival') return skyCinematicFrame(kind, age, reduced, durations);
      const state = variant === 'gilded-leviathan' ? { sector, leviathan: { sector } } : { sector };
      const frame = skyPrototypeCinematicFrame(
        kind,
        age,
        reduced,
        sector,
        descriptors(state).length,
      );
      return { ...frame, briefingWaiting: Boolean(frame.briefing && !warmed.has(key(state))) };
    },
    get(state, frame) {
      return warmed.has(key(state))
        ? prepared.get(key(state))?.[frame.briefingIndex ?? 0]
        : undefined;
    },
    release(state) {
      const id = key(state);
      for (const projection of prepared.get(id) ?? []) projection.dispose();
      prepared.delete(id);
      warmed.delete(id);
    },
    dispose() {
      for (const entries of prepared.values())
        for (const projection of entries) projection.dispose();
      prepared.clear();
      warmed.clear();
      disposed = true;
    },
  };
}
